import React, { useState, useMemo } from 'react';
import './ContractNoteGrid.css';

const PAGE_SIZE = 8;

function fmt(n) {
  if (n === undefined || n === null) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
}

function SortIcon({ col, sort }) {
  if (sort.col !== col) return <span className="sort-icon inactive">⇅</span>;
  return <span className="sort-icon active">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
}

function useSortable(defaultCol, defaultDir = 'asc') {
  const [sort, setSort] = useState({ col: defaultCol, dir: defaultDir });
  const toggle = (col) =>
    setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'desc' ? 'asc' : 'desc' }));
  return [sort, toggle];
}

function sortRows(data, col, dir, accessor) {
  return [...data].sort((a, b) => {
    const av = accessor(a, col), bv = accessor(b, col);
    if (av === bv) return 0;
    return (av < bv ? -1 : 1) * (dir === 'asc' ? 1 : -1);
  });
}

function Th({ col, label, sort, onSort }) {
  return (
    <th className="sortable-th" onClick={() => onSort(col)}>
      {label} <SortIcon col={col} sort={sort} />
    </th>
  );
}

const accessor = (row, col) => {
  switch (col) {
    case 'tradingsymbol': return row.tradingsymbol;
    case 'transaction_type': return row.transaction_type;
    case 'quantity': return row.quantity;
    case 'price': return row.price;
    default: return row.total_charges || 0;
  }
};

function ContractNoteGrid({ contractNotes = [], totalCharges = 0, onRefresh = () => {} }) {
  const [sort, toggleSort] = useSortable('tradingsymbol', 'asc');
  const [page, setPage] = useState(1);

  const sortedRows = useMemo(() => sortRows(contractNotes, sort.col, sort.dir, accessor), [contractNotes, sort]);
  const pages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="contract-note-grid">
      <div className="section-header">
        <h3>Contract Note <span className="count-badge">{contractNotes.length}</span></h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="grid-total charges">₹{fmt(totalCharges)}</span>
          <button className="refresh-btn" onClick={onRefresh} title="Refresh Contract Notes">↻</button>
        </div>
      </div>

      {contractNotes.length === 0 ? (
        <p className="no-data">No orders found for today</p>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <Th col="tradingsymbol" label="Symbol" sort={sort} onSort={toggleSort} />
                  <Th col="transaction_type" label="Type" sort={sort} onSort={toggleSort} />
                  <Th col="quantity" label="Qty" sort={sort} onSort={toggleSort} />
                  <Th col="price" label="Price" sort={sort} onSort={toggleSort} />
                  <Th col="total" label="Total" sort={sort} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((note, i) => (
                  <tr key={i}>
                    <td className="symbol-cell">{note.tradingsymbol}</td>
                    <td className={note.transaction_type === 'BUY' ? 'buy-cell' : 'sell-cell'}>
                      {note.transaction_type}
                    </td>
                    <td>{note.quantity}</td>
                    <td>₹{fmt(note.price)}</td>
                    <td className="total-cell">₹{fmt(note.total_charges)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="section-footer">
              <span className="page-range">
                {Math.min((page - 1) * PAGE_SIZE + 1, sortedRows.length)}–{Math.min(page * PAGE_SIZE, sortedRows.length)} of {sortedRows.length}
              </span>
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
                <span className="page-info">{page} / {pages}</span>
                <button disabled={page === pages} onClick={() => setPage((p) => p + 1)}>›</button>
                <button disabled={page === pages} onClick={() => setPage(pages)}>»</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ContractNoteGrid;
